<template>
  <div
    class="login-modal"
    v-loading="isLoading"
  >
    <app-i18n
      class="login-modal__title"
      code="auth.signin"
    />
    <div class="login-modal__title-separator" />

    <el-form
      v-if="model"
      class="login-modal__form login-modal__form--compact"
      :rules="rules"
      :model="model"
      ref="form"
      @validate="onFormValidate"
      @submit.prevent.native="doSubmit"
      @keypress.enter.native="doSubmit"
    >
      <el-row>
        <!-- Email -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            :label="fields.email.label"
            :prop="fields.email.name"
            :required="fields.email.required"
          >
            <el-input
              v-model="model[fields.email.name]"
              :placeholder="i18n('user.placeholders.email')"
              prefix-icon="icon-mail"
            />
          </el-form-item>
        </el-col>

        <!-- Password -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            :label="fields.password.label"
            :prop="fields.password.name"
            :required="fields.password.required"
          >
            <el-input
              v-model="model[fields.password.name]"
              class="el-input--password"
              type="password"
              show-password
              :placeholder="i18n('user.placeholders.password')"
              prefix-icon="icon-lock"
            />
          </el-form-item>
        </el-col>

        <!-- Global error message -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            class="login-modal__form__global-error"
            v-if="globalErrorMessage"
            :error="globalErrorMessage"
          />
        </el-col>

        <!-- Forgot password -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item class="login-modal__form__forgot-password el-form-item--short el-form-item--no-margin">
            <el-link
              type="primary"
              :href="null"
              @click="doForgotPassword"
            >
              <app-i18n code="auth.forgotPassword.message" />
            </el-link>
          </el-form-item>
        </el-col>

        <!-- Remember me -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            class="login-modal__form__remember-me el-form-item--short"
            :prop="fields.rememberMe.name"
            :required="fields.rememberMe.required"
          >
            <el-checkbox v-model="model[fields.rememberMe.name]">
              <app-i18n code="user.fields.rememberMe" />
            </el-checkbox>
          </el-form-item>
        </el-col>

        <!-- Form controls -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item class="login-modal__form__controls">
            <el-button
              type="primary"
              @click="doSubmit"
              :disabled="isLoading"
            >
              <app-i18n code="auth.signin" />
            </el-button>
          </el-form-item>
        </el-col>

        <!-- Sign up -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item class="login-modal__form__sign-up el-form-item--short el-form-item--no-margin">
            <div class="login-modal__form__sign-up__wrapper">
              <app-i18n code="auth.noAccountYet" />
              <el-link
                type="primary"
                :href="null"
                @click="doSignUp"
              >
                <app-i18n code="auth.signup" />
              </el-link>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'

import { login } from '@/auth'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'

import { UserModel } from '@modules/auth/user-model'

export default {
  name: 'login-modal',
  data() {
    return {
      model: {
        rememberMe: true,
      },
      globalErrorMessage: null,
      isLoading: false,
      i18n,
      siteFetchPromise: new Promise(resolve => resolve()),
      siteFetchPromiseResolve: () => {},
    }
  },
  props: {
    siteLoading: Boolean,
  },
  computed: {
    ...mapGetters({
      applyFlowSuccessCallback: 'applyFlowSuccessCallback'
    }),
    rules() {
      const { formSchema } = this;
      return formSchema.rules();
    },
    
    formSchema() {
      const { fields } = this;
      return new FormSchema([
        fields.email,
        fields.password,
        fields.rememberMe,      
      ]);
    },

    fields() {
      return new UserModel().fields();
    },
  },
  methods: {
    ...mapMutations({
      setLoginModalVisible: 'setLoginModalVisible',
      setSignupModalVisible: 'setSignupModalVisible',
      setForgotPasswordModalVisible: 'setForgotPasswordModalVisible',
      setApplyFlowSuccessCallback: 'setApplyFlowSuccessCallback',
      setLockDialog: 'setLockDialog',
    }),
    async doSubmit() {
      try {
        await this.$refs.form.validate()
      } catch (e) {
        return
      }

      try {
        this.isLoading = true
        await login(this.model)
        await this.onClose()
        this.onSuccess()
      } catch (e) {
        const { code } = e
        if (code) this.globalErrorMessage = i18n(`firebaseErrors.${code}`)
        else this.globalErrorMessage = i18n('common.unknownError')
      } finally {
        this.isLoading = false
      }
    },
    async doSignUp() {
      this.onSignUp()
      await this.$nextTick()
      this.onClose()
    },
    async doForgotPassword() {
      this.onForgotPassword()
      await this.$nextTick()
      this.onClose()
    },
    onSuccess() {
      if (typeof this.applyFlowSuccessCallback !== 'function') return
      this.applyFlowSuccessCallback()
      this.setApplyFlowSuccessCallback(null)
    },
    async onClose() {
      await this.$nextTick()
      await this.siteFetchPromise
      this.setLoginModalVisible(false)
    },
    onSignUp() {
      this.setSignupModalVisible(true)
    },
    onForgotPassword() {
      this.setForgotPasswordModalVisible(true)
    },
    onLoadingChange(isLoading) {
      this.setLockDialog(isLoading)
    },
    onFormValidate() {
      this.globalErrorMessage = null
    },
  },
  watch: {
    isLoading: {
      handler: 'onLoadingChange',
      immediate: true,
    },
    siteLoading(val) {
      if (val) {
        this.siteFetchPromise = new Promise(resolve => this.siteFetchPromiseResolve = resolve)
      } else {
        this.siteFetchPromiseResolve()
        this.siteFetchPromiseResolve = () => {}
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.login-modal {

  @include modal-mixin;


  &__form {
    &__forgot-password {
      text-align: right;

      span {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: -0.25px;
        color: #2acccc;
      }
    }

    &__remember-me {
      text-align: left;

      span {
        margin-left: 10px;
        font-size: 14px;
        font-weight: normal;
        letter-spacing: -0.3px;
        color: #333333;
      }

      @include mobile {
        margin-top: 16px;
      }
    }

    &__sign-up {
      &__wrapper {
        display: flex;
        align-content: center;
        justify-content: center;
      }

      .el-link {
        margin-left: 5px;
      }
    }
  }

}
</style>
